import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Star, Menu, X, ChevronRight, Send, Sparkles, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { toast } from 'sonner';
import { format, parseISO, isSameDay } from 'date-fns';
import { API, useAuth } from '@/App';
import { BookingCalendar } from '@/components/BookingCalendar';
import { FeedbackForm } from '@/components/FeedbackForm';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_5b06bfd4-5798-4290-968c-c27d6c6d2c81/artifacts/krjgv0n0_LOGO01.png";

const BUSINESS_INFO = {
  name: "Healing Hands",
  tagline: "Restoring Body & Soul",
  owner: "Lucinda Rodrigues",
  phone: "(857) 333 5231",
  email: "lucindasilva1015@gmail.com",
  address: "735 William T Morrissey Boulevard, Dorchester MA 02122",
  hours: "Mon - Sun: 10AM - 9PM",
  description: "At Healing Hands, we melt away your stress and restore your natural rhythm through expert, intuitive touch."
};

const PAYMENT_METHODS = [
  { name: "Venmo", handle: "@Lucinda-Rodrigues-1", color: "#3D95CE", icon: "V" },
  { name: "Cash App", handle: "$LucyOlaf", color: "#00D632", icon: "$" },
  { name: "Zelle", handle: "(857) 333 5231", color: "#6D1ED4", icon: "Z" }
];

const ALL_TIME_SLOTS = [
  "10:00 AM", "11:00 AM", "12:00 PM",
  "1:00 PM", "2:00 PM", "3:00 PM", "4:00 PM",
  "5:00 PM", "6:00 PM", "7:00 PM", "8:00 PM", "9:00 PM"
];

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [lockedSlots, setLockedSlots] = useState([]);
  const [blockedSlots, setBlockedSlots] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    massage_type: '',
    duration: '',
    price: 0,
    preferred_date: null,
    preferred_time: '',
    location_type: 'mobile',
    client_address: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [bookingStep, setBookingStep] = useState(1); // 1=info, 2=deposit, 3=done
  const [depositConfirmed, setDepositConfirmed] = useState(false);

  const fetchServices = useCallback(async () => {
    try {
      const response = await fetch(`${API}/services`);
      const data = await response.json();
      setServices(data);
    } catch (error) {
      console.error('Failed to fetch services:', error);
    }
  }, []);

  const fetchTestimonials = useCallback(async () => {
    try {
      const response = await fetch(`${API}/feedback/approved`);
      const data = await response.json();
      setTestimonials(data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  }, []);

  const fetchLockedDates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/bookings/locked-dates`);
      const data = await response.json();
      setLockedSlots(data.locked_slots || []);
      setBlockedSlots(data.blocked_slots || []);
    } catch (error) {
      console.error('Failed to fetch locked dates:', error);
    }
  }, []);

  const fetchSchedule = useCallback(async () => {
    try {
      const response = await fetch(`${API}/schedule`);
      const data = await response.json();
      setSchedule(data);
    } catch (error) {
      console.error('Failed to fetch schedule:', error);
    }
  }, []);

  useEffect(() => {
    fetchServices();
    fetchTestimonials();
    fetchLockedDates();
    fetchSchedule();
  }, [fetchServices, fetchTestimonials, fetchLockedDates, fetchSchedule]);

  // Auto-fill booking form when client is logged in
  useEffect(() => {
    if (user && user.role === 'client') {
      setBookingForm(prev => ({
        ...prev,
        client_name: prev.client_name || user.name || '',
        client_email: prev.client_email || user.email || '',
        client_phone: prev.client_phone || user.phone || ''
      }));
    }
  }, [user]);

  // Dynamic: check if a day is a working day based on admin schedule
  const isDayAvailable = (date) => {
    if (!date || schedule.length === 0) return true; // default open until schedule loads
    const dayName = DAY_NAMES[date.getDay()];
    const daySchedule = schedule.find(s => s.day === dayName);
    return daySchedule ? daySchedule.enabled : true;
  };

  // Dynamic: get time slots for a given date based on admin schedule
  const getTimeSlotsForDay = (date) => {
    if (!date) return [];
    const dayName = DAY_NAMES[date.getDay()];
    const daySchedule = schedule.find(s => s.day === dayName);
    if (!daySchedule || !daySchedule.enabled) return [];
    const startIdx = ALL_TIME_SLOTS.indexOf(daySchedule.start_time);
    const endIdx = ALL_TIME_SLOTS.indexOf(daySchedule.end_time);
    if (startIdx < 0 || endIdx < 0) return ALL_TIME_SLOTS;
    return ALL_TIME_SLOTS.slice(startIdx, endIdx + 1);
  };

  // Check if a date is entirely blocked by admin
  const isDayBlocked = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return blockedSlots.some(slot => slot.date === dateStr && !slot.time);
  };

  // Check if a specific time slot is blocked by admin
  const isTimeBlocked = (date, time) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    // Blocked if entire day is blocked OR specific time is blocked
    return blockedSlots.some(slot => 
      slot.date === dateStr && (!slot.time || slot.time === time)
    );
  };

  // Check if a specific date/time slot is locked (booked)
  const isSlotLocked = (date, time) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return lockedSlots.some(slot => 
      slot.confirmed_date === dateStr && slot.confirmed_time === time
    ) || isTimeBlocked(date, time);
  };

  // Check if a date has any available slots
  const isDayFullyBooked = (date) => {
    if (isDayBlocked(date)) return true;
    const dateStr = format(date, 'yyyy-MM-dd');
    const slotsForDay = getTimeSlotsForDay(date);
    const bookedCount = slotsForDay.filter(time => {
      const isBooked = lockedSlots.some(slot => slot.confirmed_date === dateStr && slot.confirmed_time === time);
      const isBlocked = blockedSlots.some(slot => slot.date === dateStr && (slot.time === time || !slot.time));
      return isBooked || isBlocked;
    }).length;
    return slotsForDay.length > 0 && bookedCount >= slotsForDay.length;
  };

  // Get available time slots for a date (excluding locked and blocked)
  const getAvailableTimeSlots = (date) => {
    if (!date) return [];
    if (isDayBlocked(date)) return [];
    const allSlots = getTimeSlotsForDay(date);
    const dateStr = format(date, 'yyyy-MM-dd');
    return allSlots.filter(time => {
      const isBooked = lockedSlots.some(slot => slot.confirmed_date === dateStr && slot.confirmed_time === time);
      const isBlocked = blockedSlots.some(slot => slot.date === dateStr && (slot.time === time || !slot.time));
      return !isBooked && !isBlocked;
    });
  };

  const handleServiceSelect = (service) => {
    setBookingForm(prev => ({
      ...prev,
      massage_type: service.name,
      duration: service.duration,
      price: service.price
    }));
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    
    if (!bookingForm.client_name || !bookingForm.client_email || !bookingForm.client_phone || 
        !bookingForm.massage_type || !bookingForm.preferred_date || !bookingForm.preferred_time) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (bookingForm.location_type === 'mobile' && !bookingForm.client_address) {
      toast.error('Please provide your address for mobile massage');
      return;
    }

    // Check if slot is still available
    if (isSlotLocked(bookingForm.preferred_date, bookingForm.preferred_time)) {
      toast.error('This time slot has been booked. Please select another time.');
      setBookingForm(prev => ({ ...prev, preferred_time: '' }));
      fetchLockedDates();
      return;
    }

    // Go to deposit step
    setBookingStep(2);
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleDepositConfirmAndSubmit = async () => {
    if (!depositConfirmed) {
      toast.error('Please confirm you have sent the $25 deposit');
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/bookings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...bookingForm,
          preferred_date: format(bookingForm.preferred_date, 'yyyy-MM-dd')
        })
      });

      if (!response.ok) throw new Error('Booking failed');

      setBookingStep(3);
      fetchLockedDates();
    } catch (error) {
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetBookingFlow = () => {
    setBookingStep(1);
    setDepositConfirmed(false);
    setBookingForm({
      client_name: '',
      client_email: '',
      client_phone: '',
      massage_type: '',
      duration: '',
      price: 0,
      preferred_date: null,
      preferred_time: '',
      location_type: 'mobile',
      client_address: '',
      notes: ''
    });
  };

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileMenuOpen(false);
  };

  const availableTimeSlots = getAvailableTimeSlots(bookingForm.preferred_date);

  return (
    <div className="min-h-screen bg-healing-cream">
      {/* Header */}
      <header className="sticky-header border-b border-healing-border" data-testid="header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-3" data-testid="logo">
              <img src={LOGO_URL} alt="Healing Hands Logo" className="h-14 w-auto" />
            </div>

            <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
              <button onClick={() => scrollToSection('services')} className="text-healing-burgundy-light hover:text-healing-purple transition-colors font-body">
                Services
              </button>
              <button onClick={() => scrollToSection('booking')} className="text-healing-burgundy-light hover:text-healing-purple transition-colors font-body">
                Book Now
              </button>
              <button onClick={() => scrollToSection('payment')} className="text-healing-burgundy-light hover:text-healing-purple transition-colors font-body">
                Payment
              </button>
              <button onClick={() => scrollToSection('testimonials')} className="text-healing-burgundy-light hover:text-healing-purple transition-colors font-body">
                Reviews
              </button>
              <button onClick={() => scrollToSection('contact')} className="text-healing-burgundy-light hover:text-healing-purple transition-colors font-body">
                Contact
              </button>
              <Button 
                onClick={() => navigate('/account')}
                variant="outline" 
                size="sm"
                className="border-healing-purple text-healing-purple hover:bg-healing-purple hover:text-white"
                data-testid="client-account-btn-desktop"
              >
                My Account
              </Button>
            </nav>

            <button 
              className="md:hidden text-healing-burgundy"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden bg-healing-cream border-t border-healing-border" data-testid="mobile-nav">
            <div className="px-4 py-4 space-y-3">
              <button onClick={() => scrollToSection('services')} className="block w-full text-left py-2 text-healing-burgundy-light">Services</button>
              <button onClick={() => scrollToSection('booking')} className="block w-full text-left py-2 text-healing-burgundy-light">Book Now</button>
              <button onClick={() => scrollToSection('payment')} className="block w-full text-left py-2 text-healing-burgundy-light">Payment</button>
              <button onClick={() => scrollToSection('testimonials')} className="block w-full text-left py-2 text-healing-burgundy-light">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-healing-burgundy-light">Contact</button>
              <Button onClick={() => navigate('/account')} variant="outline" size="sm" className="w-full border-healing-purple text-healing-purple">
                My Account
              </Button>
            </div>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="relative py-24 md:py-32 overflow-hidden" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-healing-cream via-healing-cream-alt to-healing-cream opacity-90"></div>
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1745327883508-b6cd32e5dde5?w=1920)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <p className="font-accent text-xs tracking-widest uppercase text-healing-purple mb-4 animate-fade-in-up">
              Massage Therapist
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-healing-burgundy mb-6 animate-fade-in-up animate-delay-100">
              {BUSINESS_INFO.name}
            </h1>
            <p className="font-heading text-xl sm:text-2xl text-healing-purple-dark italic mb-6 animate-fade-in-up animate-delay-200">
              {BUSINESS_INFO.tagline}
            </p>
            <p className="font-body text-healing-burgundy-light text-lg mb-8 max-w-2xl animate-fade-in-up animate-delay-300">
              {BUSINESS_INFO.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animate-delay-400">
              <Button 
                onClick={() => scrollToSection('booking')}
                size="lg"
                className="bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full px-8"
                data-testid="book-now-btn"
              >
                Book Your Session
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
              <Button 
                onClick={() => scrollToSection('services')}
                variant="outline"
                size="lg"
                className="border-healing-purple text-healing-purple hover:bg-healing-purple hover:text-white rounded-full px-8"
                data-testid="view-services-btn"
              >
                View Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 md:py-32 bg-white" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-accent text-xs tracking-widest uppercase text-healing-purple mb-4">Our Services</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-healing-burgundy mb-4">
              Massage Therapy
            </h2>
            <p className="font-body text-healing-burgundy-light max-w-2xl mx-auto">
              Choose from our range of therapeutic massage services, each designed to address your specific needs.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <div 
                key={service.id}
                className="service-card bg-healing-cream-alt border border-healing-border rounded-2xl p-6 cursor-pointer"
                onClick={() => handleServiceSelect(service)}
                data-testid={`service-card-${service.id}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-heading text-xl text-healing-burgundy mb-1">{service.name}</h3>
                    <p className="font-body text-sm text-healing-burgundy-light">{service.duration}</p>
                  </div>
                  <Sparkles className="h-5 w-5 text-healing-purple" />
                </div>
                <p className="font-body text-sm text-healing-burgundy-light mb-4">{service.description}</p>
                <div className="flex items-center justify-between">
                  <span className="font-heading text-2xl text-healing-purple">${service.price}</span>
                  <Button 
                    size="sm" 
                    className="bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full"
                    data-testid={`book-service-${service.id}`}
                  >
                    Book
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Booking Section */}
      <section id="booking" className="py-24 md:py-32 bg-healing-cream" data-testid="booking-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-accent text-xs tracking-widest uppercase text-healing-purple mb-4">Schedule Your Visit</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-healing-burgundy mb-4">
              Book an Appointment
            </h2>
            <p className="font-body text-healing-burgundy-light">
              {bookingStep === 1 && 'Select your service and preferred date.'}
              {bookingStep === 2 && 'Complete the $25 deposit to confirm your booking.'}
              {bookingStep === 3 && 'Your booking has been submitted!'}
            </p>
          </div>

          {/* Step indicators */}
          <div className="flex items-center justify-center gap-2 mb-10">
            {[1, 2, 3].map(step => (
              <div key={step} className="flex items-center gap-2">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  bookingStep >= step 
                    ? 'bg-healing-purple text-white' 
                    : 'bg-healing-border text-healing-burgundy-light'
                }`}>
                  {bookingStep > step ? <Check className="h-4 w-4" /> : step}
                </div>
                <span className={`text-xs font-body hidden sm:inline ${bookingStep >= step ? 'text-healing-purple' : 'text-healing-burgundy-light'}`}>
                  {step === 1 && 'Details'}
                  {step === 2 && 'Deposit'}
                  {step === 3 && 'Confirmed'}
                </span>
                {step < 3 && <div className={`w-12 h-0.5 ${bookingStep > step ? 'bg-healing-purple' : 'bg-healing-border'}`} />}
              </div>
            ))}
          </div>

          {/* STEP 1: Booking Details */}
          {bookingStep === 1 && (
            <form onSubmit={handleBookingSubmit} className="bg-white rounded-2xl p-8 md:p-12 border border-healing-border shadow-sm" data-testid="booking-step-1">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="client_name" className="text-healing-burgundy">Full Name *</Label>
                  <Input
                    id="client_name"
                    value={bookingForm.client_name}
                    onChange={(e) => setBookingForm(prev => ({ ...prev, client_name: e.target.value }))}
                    placeholder="Your name"
                    className="border-healing-border focus:ring-healing-purple"
                    data-testid="booking-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_email" className="text-healing-burgundy">Email *</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={bookingForm.client_email}
                    onChange={(e) => setBookingForm(prev => ({ ...prev, client_email: e.target.value }))}
                    placeholder="your@email.com"
                    className="border-healing-border focus:ring-healing-purple"
                    data-testid="booking-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="client_phone" className="text-healing-burgundy">Phone *</Label>
                  <Input
                    id="client_phone"
                    type="tel"
                    value={bookingForm.client_phone}
                    onChange={(e) => setBookingForm(prev => ({ ...prev, client_phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="border-healing-border focus:ring-healing-purple"
                    data-testid="booking-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-healing-burgundy">Service *</Label>
                  <Select 
                    value={bookingForm.massage_type ? `${bookingForm.massage_type}|${bookingForm.duration}|${bookingForm.price}` : ''}
                    onValueChange={(value) => {
                      const [name, duration, price] = value.split('|');
                      setBookingForm(prev => ({ ...prev, massage_type: name, duration, price: parseFloat(price) }));
                    }}
                  >
                    <SelectTrigger className="border-healing-border" data-testid="booking-service-select">
                      <SelectValue placeholder="Select a service" />
                    </SelectTrigger>
                    <SelectContent>
                      {services.map(service => (
                        <SelectItem 
                          key={service.id} 
                          value={`${service.name}|${service.duration}|${service.price}`}
                        >
                          {service.name} - {service.duration} (${service.price})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Airbnb-style inline calendar */}
                <BookingCalendar
                  selectedDate={bookingForm.preferred_date}
                  selectedTime={bookingForm.preferred_time}
                  onDateSelect={(date) => setBookingForm(prev => ({ ...prev, preferred_date: date, preferred_time: '' }))}
                  onTimeSelect={(time) => setBookingForm(prev => ({ ...prev, preferred_time: time }))}
                  isDayAvailable={isDayAvailable}
                  isDayFullyBooked={isDayFullyBooked}
                  isDayBlocked={isDayBlocked}
                  getTimeSlotsForDay={getTimeSlotsForDay}
                  availableTimeSlots={availableTimeSlots}
                />

                <div className="md:col-span-2 space-y-4">
                  <Label className="text-healing-burgundy">Location *</Label>
                  <RadioGroup
                    value={bookingForm.location_type}
                    onValueChange={(value) => setBookingForm(prev => ({ ...prev, location_type: value }))}
                    className="flex gap-6"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="mobile" id="mobile" data-testid="location-mobile" />
                      <Label htmlFor="mobile" className="cursor-pointer">Mobile (We come to you)</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="studio" id="studio" data-testid="location-studio" />
                      <Label htmlFor="studio" className="cursor-pointer">Studio</Label>
                    </div>
                  </RadioGroup>
                </div>

                {bookingForm.location_type === 'mobile' && (
                  <div className="md:col-span-2 space-y-2">
                    <Label htmlFor="client_address" className="text-healing-burgundy">Your Address *</Label>
                    <Input
                      id="client_address"
                      value={bookingForm.client_address}
                      onChange={(e) => setBookingForm(prev => ({ ...prev, client_address: e.target.value }))}
                      placeholder="Enter your full address"
                      className="border-healing-border focus:ring-healing-purple"
                      data-testid="booking-address-input"
                    />
                  </div>
                )}

                {bookingForm.location_type === 'studio' && (
                  <div className="md:col-span-2 p-4 bg-healing-cream-alt rounded-xl">
                    <p className="font-body text-sm text-healing-burgundy-light flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-healing-purple" />
                      Studio Location: {BUSINESS_INFO.address}
                    </p>
                  </div>
                )}

                <div className="md:col-span-2 space-y-2">
                  <Label htmlFor="notes" className="text-healing-burgundy">Additional Notes</Label>
                  <Textarea
                    id="notes"
                    value={bookingForm.notes}
                    onChange={(e) => setBookingForm(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="Any special requests or health concerns?"
                    className="border-healing-border focus:ring-healing-purple min-h-[100px]"
                    data-testid="booking-notes-input"
                  />
                </div>

                {bookingForm.price > 0 && (
                  <div className="md:col-span-2 p-4 bg-healing-purple/10 rounded-xl">
                    <p className="font-heading text-xl text-healing-burgundy">
                      Total: <span className="text-healing-purple">${bookingForm.price}</span>
                      <span className="text-sm text-healing-burgundy-light ml-2">+ $25 deposit required</span>
                    </p>
                  </div>
                )}

                <div className="md:col-span-2">
                  <Button 
                    type="submit" 
                    className="w-full bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full py-6"
                    data-testid="submit-booking-btn"
                  >
                    Continue to Deposit
                    <ChevronRight className="ml-2 h-5 w-5" />
                  </Button>
                </div>
              </div>
            </form>
          )}

          {/* STEP 2: Mandatory Deposit Confirmation */}
          {bookingStep === 2 && (
            <div className="bg-white rounded-2xl p-8 md:p-12 border border-healing-border shadow-sm" data-testid="booking-step-2">
              {/* Booking summary */}
              <div className="mb-8 p-5 bg-healing-cream-alt rounded-xl border border-healing-border">
                <h3 className="font-heading text-lg text-healing-burgundy mb-3">Your Appointment</h3>
                <div className="grid grid-cols-2 gap-3 text-sm font-body text-healing-burgundy-light">
                  <p><strong>Service:</strong> {bookingForm.massage_type}</p>
                  <p><strong>Duration:</strong> {bookingForm.duration}</p>
                  <p><strong>Date:</strong> {bookingForm.preferred_date && format(bookingForm.preferred_date, 'EEEE, MMM d, yyyy')}</p>
                  <p><strong>Time:</strong> {bookingForm.preferred_time}</p>
                  <p><strong>Price:</strong> ${bookingForm.price}</p>
                  <p><strong>Location:</strong> {bookingForm.location_type === 'mobile' ? 'Mobile' : 'Studio'}</p>
                </div>
              </div>

              {/* Deposit payment instructions */}
              <div className="space-y-6">
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-heading text-lg text-amber-900">$25 Deposit Required</p>
                      <p className="font-body text-sm text-amber-700 mt-1">
                        A <strong>$25 non-refundable deposit</strong> is mandatory to confirm your appointment. 
                        Please send the deposit using one of the methods below <strong>before</strong> completing your booking.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <p className="font-body text-sm font-medium text-healing-burgundy">Send your $25 deposit to:</p>
                  {PAYMENT_METHODS.map((method) => (
                    <div key={method.name} className="flex items-center gap-4 bg-healing-cream-alt rounded-xl p-4 border border-healing-border">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0"
                        style={{ backgroundColor: method.color }}
                      >
                        {method.icon}
                      </div>
                      <div>
                        <p className="font-body font-medium text-healing-burgundy">{method.name}</p>
                        <p className="font-body text-healing-purple font-semibold">{method.handle}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* No-show policy */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                  <p className="font-body text-sm text-red-800">
                    <strong>No-Show Policy:</strong> If you fail to show up for your appointment without prior notice (24 hrs), 
                    the $25 deposit will <strong>not</strong> be refunded.
                  </p>
                </div>

                {/* Mandatory confirmation checkbox */}
                <label className="flex items-start gap-3 cursor-pointer p-4 rounded-xl border-2 transition-colors select-none
                  ${depositConfirmed ? 'border-healing-purple bg-healing-purple/5' : 'border-healing-border'}
                " data-testid="deposit-confirm-label">
                  <input
                    type="checkbox"
                    checked={depositConfirmed}
                    onChange={(e) => setDepositConfirmed(e.target.checked)}
                    className="mt-1 w-5 h-5 accent-healing-purple rounded"
                    data-testid="deposit-confirm-checkbox"
                  />
                  <span className="font-body text-sm text-healing-burgundy">
                    I confirm that I have sent the <strong>$25 deposit</strong> via Venmo, Cash App, or Zelle. 
                    I understand that in case of a no-show, the deposit is non-refundable.
                  </span>
                </label>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setBookingStep(1)}
                    className="flex-1 border-healing-border rounded-full py-6"
                    data-testid="booking-back-btn"
                  >
                    Back
                  </Button>
                  <Button
                    onClick={handleDepositConfirmAndSubmit}
                    disabled={!depositConfirmed || submitting}
                    className="flex-[2] bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full py-6 disabled:opacity-50"
                    data-testid="booking-confirm-submit"
                  >
                    {submitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Confirming...
                      </>
                    ) : (
                      <>
                        <Check className="mr-2 h-5 w-5" />
                        Confirm Booking
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* STEP 3: Confirmation */}
          {bookingStep === 3 && (
            <div className="bg-white rounded-2xl p-8 md:p-12 border border-healing-border shadow-sm text-center" data-testid="booking-step-3">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="h-10 w-10 text-green-600" />
              </div>
              <h3 className="font-heading text-2xl text-healing-burgundy mb-3">Booking Confirmed!</h3>
              <p className="font-body text-healing-burgundy-light mb-6 max-w-md mx-auto">
                Thank you for your deposit. Your appointment request has been submitted and we'll contact you shortly to confirm.
              </p>
              <Button
                onClick={resetBookingFlow}
                className="bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full px-8"
                data-testid="booking-new-btn"
              >
                Book Another Appointment
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Payment Section */}
      <section id="payment" className="py-24 md:py-32 bg-white" data-testid="payment-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-accent text-xs tracking-widest uppercase text-healing-purple mb-4">Payment Options</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-healing-burgundy mb-4">
              Payment Methods
            </h2>
            <p className="font-body text-healing-burgundy-light">
              We accept the following payment methods for your convenience.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PAYMENT_METHODS.map((method) => (
              <div 
                key={method.name}
                className="bg-healing-cream-alt border border-healing-border rounded-2xl p-8 text-center"
                data-testid={`payment-${method.name.toLowerCase().replace(' ', '-')}`}
              >
                <div 
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center text-white text-2xl font-bold"
                  style={{ backgroundColor: method.color }}
                >
                  {method.icon}
                </div>
                <h3 className="font-heading text-xl text-healing-burgundy mb-2">{method.name}</h3>
                <p className="font-body text-healing-purple font-medium">{method.handle}</p>
              </div>
            ))}
          </div>

          <p className="text-center mt-8 font-body text-healing-burgundy-light text-sm">
            Payment is due at the time of service or upon booking confirmation.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-24 md:py-32 bg-healing-cream" data-testid="testimonials-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-accent text-xs tracking-widest uppercase text-healing-purple mb-4">Client Feedback</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-healing-burgundy mb-4">
              What Our Clients Say
            </h2>
          </div>

          {testimonials.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.feedback_id}
                  className="testimonial-card p-6"
                  data-testid={`testimonial-${testimonial.feedback_id}`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`h-5 w-5 ${i < testimonial.rating ? 'star-filled fill-current' : 'star-empty'}`} 
                      />
                    ))}
                  </div>
                  <p className="font-body text-healing-burgundy-light mb-4 italic">"{testimonial.message}"</p>
                  <p className="font-heading text-healing-burgundy">— {testimonial.client_name}</p>
                  {testimonial.massage_type && (
                    <p className="font-body text-sm text-healing-purple">{testimonial.massage_type}</p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center mb-12 py-8">
              <p className="font-body text-healing-burgundy-light">Be the first to leave a review!</p>
            </div>
          )}

          <FeedbackForm services={services} />
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 md:py-32 bg-white" data-testid="contact-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="font-accent text-xs tracking-widest uppercase text-healing-purple mb-4">Get In Touch</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-healing-burgundy mb-4">
              Contact Us
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-healing-purple/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="h-5 w-5 text-healing-purple" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-healing-burgundy mb-1">Phone</h3>
                  <a href={`tel:${BUSINESS_INFO.phone.replace(/[^0-9]/g, '')}`} className="font-body text-healing-burgundy-light hover:text-healing-purple">
                    {BUSINESS_INFO.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-healing-purple/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="h-5 w-5 text-healing-purple" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-healing-burgundy mb-1">Email</h3>
                  <a href={`mailto:${BUSINESS_INFO.email}`} className="font-body text-healing-burgundy-light hover:text-healing-purple">
                    {BUSINESS_INFO.email}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-healing-purple/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-healing-purple" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-healing-burgundy mb-1">Studio Location</h3>
                  <p className="font-body text-healing-burgundy-light">{BUSINESS_INFO.address}</p>
                  <p className="font-body text-sm text-healing-purple mt-1">* Mobile massage available - we come to you!</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-healing-purple/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="h-5 w-5 text-healing-purple" />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-healing-burgundy mb-1">Business Hours</h3>
                  <p className="font-body text-healing-burgundy-light">{BUSINESS_INFO.hours}</p>
                  <p className="font-body text-sm text-healing-purple mt-1">Monday - Sunday</p>
                </div>
              </div>
            </div>

            <div className="bg-healing-cream-alt rounded-2xl p-8 border border-healing-border">
              <h3 className="font-heading text-xl text-healing-burgundy mb-4">Quick Contact</h3>
              <p className="font-body text-healing-burgundy-light mb-6">
                For appointments, call us directly or use the booking form above. We'll get back to you as soon as possible.
              </p>
              <div className="space-y-4">
                <Button 
                  onClick={() => window.location.href = `tel:${BUSINESS_INFO.phone.replace(/[^0-9]/g, '')}`}
                  className="w-full bg-healing-purple hover:bg-healing-purple-hover text-white rounded-full"
                  data-testid="call-now-btn"
                >
                  <Phone className="mr-2 h-4 w-4" />
                  Call Now
                </Button>
                <Button 
                  onClick={() => window.location.href = `mailto:${BUSINESS_INFO.email}`}
                  variant="outline"
                  className="w-full border-healing-purple text-healing-purple hover:bg-healing-purple hover:text-white rounded-full"
                  data-testid="email-now-btn"
                >
                  <Mail className="mr-2 h-4 w-4" />
                  Send Email
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 bg-healing-burgundy text-white" data-testid="footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Healing Hands" className="h-12 w-auto brightness-0 invert" />
            </div>
            <p className="font-body text-sm text-white/70 text-center">
              © {new Date().getFullYear()} Healing Hands Massage Therapy. All rights reserved.
            </p>
            <div className="flex gap-4">
              <a href={`tel:${BUSINESS_INFO.phone.replace(/[^0-9]/g, '')}`} className="text-white/70 hover:text-white">
                <Phone className="h-5 w-5" />
              </a>
              <a href={`mailto:${BUSINESS_INFO.email}`} className="text-white/70 hover:text-white">
                <Mail className="h-5 w-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
